import { useEffect, useState } from "react";

import { readChangeAudit, type ChangeAudit } from "../api/changeAudit";
import { messageOf } from "../api/http";

/** The server's execution state, reread while it can still change without another event. */
export function useChangeAudit(changeId: string) {
  const [audit, setAudit] = useState<ChangeAudit | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setAudit(null);
    setError(null);
    const read = async () => {
      try {
        const value = await readChangeAudit(changeId);
        if (!live) return;
        setAudit(value);
        if (value.status === "approved" || value.status === "applying") timer = setTimeout(read, 15_000);
      } catch (reason) {
        if (live) setError(messageOf(reason));
      }
    };
    void read();
    return () => { live = false; clearTimeout(timer); };
  }, [changeId]);
  return { audit, error };
}

function period(seconds: number): string {
  if (seconds === 3600) return "hourly run";
  if (seconds % 3600 === 0) return `run every ${seconds / 3600} hours`;
  if (seconds % 60 === 0) return `run every ${seconds / 60} minutes`;
  return `run every ${seconds} seconds`;
}

export function ChangeProgress({ audit }: { audit: ChangeAudit }) {
  const sentence = audit.status === "approved" && audit.waiting_for_sweep && audit.sweep_period_seconds !== null
    ? `Approved, waiting for the next ${period(audit.sweep_period_seconds)}`
    : audit.status === "applying" && audit.slow ? "Applying, taking longer than expected" : null;
  return sentence === null ? null : <p role="status">{sentence}</p>;
}
